import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import MainContentStyled from './MainContentStyled';
import Customization from '../Customization';
import Loader from 'ui-component/Loader';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';

import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const {
    state: { borderRadius, miniDrawer }
  } = useConfig();
  const { menuMaster, menuMasterLoading } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  useEffect(() => {
    handlerDrawerOpen(!miniDrawer);
  }, [miniDrawer]);

  useEffect(() => {
    downMD && handlerDrawerOpen(false);
  }, [downMD]);

  // Real-time security sync: Listen for system-wide force logout and 2FA state changes
  useEffect(() => {
    let socket = null;
    try {
      import('socket.io-client').then(({ io }) => {
        socket = io({
          path: '/socket.io',
          transports: ['websocket', 'polling'],
          reconnectionAttempts: 5
        });

        socket.on('FORCE_LOGOUT', (data) => {
          import('services/api').then(({ handleInstantLogout }) => {
            handleInstantLogout(data?.reason || "Two-Factor Authentication was modified system-wide. All active sessions have been terminated.");
          });
        });

        socket.on('2FA_STATUS_CHANGE', (data) => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('2fa-status-change', { detail: data }));
          }
        });
      }).catch(() => {});
    } catch (_) {}

    return () => {
      if (socket) {
        try { socket.disconnect(); } catch (_) {}
      }
    };
  }, []);

  // horizontal menu-list bar : drawer

  if (menuMasterLoading) return <Loader />;

  return (
    <Box sx={{ display: 'flex' }}>
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.header || '#15171C') : '#FFFFFF',
          borderBottom: theme.palette.mode === 'dark' ? (theme.palette.border?.default ? `1px solid ${theme.palette.border.default}` : '1px solid rgba(255, 255, 255, 0.08)') : '1px solid #E2E8F0',
          height: 72,
          justifyContent: 'center',
          boxShadow: 'none',
          zIndex: 1100
        }}
      >
        <Toolbar sx={{ height: 72, minHeight: '72px !important', px: { xs: 1.5, sm: 2, md: 3 } }}>
          <Header />
        </Toolbar>
      </AppBar>

      {/* menu / drawer */}
      <Sidebar />

      {/* main content */}
      <MainContentStyled {...{ borderRadius, open: drawerOpen }}>
        <Box sx={{ ...{ px: { xs: 0 } }, minHeight: 'calc(100vh - 128px)', display: 'flex', flexDirection: 'column' }}>
          {/* breadcrumb */}
          <Box sx={{ mt: 1 }}>
            <Breadcrumbs />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Outlet />
          </Box>
          <Footer />
        </Box>
      </MainContentStyled>
      {/* <Customization /> */}
    </Box>
  );
}
